import Papa from 'papaparse';
import * as XLSX from 'xlsx';

export const MAX_IMPORT_ROWS = 100;
export const MAX_IMPORT_BYTES = 5 * 1024 * 1024;
const field = (key, label, required = false, hint = '') => ({ key, label, required, hint });
const identity = [field('accountId', 'Customer account ID', true, 'A stable ID from your accounting system; names alone are not matched.'), field('name', 'Company name', true)];
const invoiceIdentity = [field('documentNumber', 'Invoice number', true), field('currency', 'Currency', true, 'Explicit ISO currency code, for example GBP or ILS.')];
export const IMPORT_FIELDS = {
  invoices: [...identity, ...invoiceIdentity, field('invoiceAmount', 'Invoice amount', true, 'Nonnegative decimal amount, without currency symbols or thousands separators.'), field('invoiceDate', 'Invoice date', true, 'YYYY-MM-DD'), field('dueDate', 'Due date', true, 'YYYY-MM-DD')],
  payments: [identity[0], field('name', 'Company name'), field('documentNumber', 'Invoice number', false, 'If missing or unmatched, the payment remains unallocated for review.'), invoiceIdentity[1], field('eventId', 'Transaction ID', true, 'A stable, unique source transaction ID prevents duplicate payments.'), field('amount', 'Transaction amount', true, 'Positive decimal amount. Use transaction type for credits or reversals.'), field('paymentDate', 'Transaction date', true, 'YYYY-MM-DD'), field('kind', 'Transaction type', false, 'payment, credit or reversal. Unmapped means payment.'), field('reversesEventId', 'Reversed transaction ID', false, 'Required for a reversal; identifies the original transaction.')],
  aging: [...identity, ...invoiceIdentity, field('amountDue', 'Outstanding amount', true, 'Signed decimal balance as of the selected date.'), field('invoiceAmount', 'Original invoice amount'), field('invoiceDate', 'Invoice date', false, 'YYYY-MM-DD'), field('dueDate', 'Due date', false, 'YYYY-MM-DD')],
  aging_summary: [...identity, invoiceIdentity[1], field('amountDue', 'Total customer balance', true, 'Total for this customer and currency as of the selected date.'), field('currentAmount', 'Current / not yet due', true, 'Due today or later. Enter 0 only when the source explicitly reports zero.'), field('aging30Amount', '1–30 days overdue', true), field('aging60Amount', '31–60 days overdue', true), field('aging90Amount', '61–90 days overdue', true), field('aging90PlusAmount', 'More than 90 days overdue', true, '91 or more calendar days past due.'), field('unknownDueDateAmount', 'Due date unknown', false, 'Map a separately reported unknown-age balance. Declared buckets must add up exactly to the total.')],
  allocations: [identity[0], invoiceIdentity[1], field('eventId', 'Transaction ID', true), invoiceIdentity[0]],
};
const normalized = value => String(value || '').trim().toLowerCase().replace(/[^a-z0-9]/g, '');
const aliases = {
  accountId: ['accountid', 'customerid', 'customeraccountid', 'customeraccountnumber', 'accountnumber'],
  name: ['name', 'companyname', 'customername'],
  documentNumber: ['documentnumber', 'invoicenumber', 'invoiceid'],
  currency: ['currency', 'currencycode'], invoiceAmount: ['invoiceamount', 'originalinvoiceamount'],
  invoiceDate: ['invoicedate', 'issuedate'], dueDate: ['duedate'],
  eventId: ['eventid', 'transactionid', 'paymentid'], amount: ['amount', 'transactionamount', 'paymentamount'],
  paymentDate: ['paymentdate', 'transactiondate'], kind: ['kind', 'transactiontype'],
  reversesEventId: ['reverseseventid', 'reversedtransactionid'], amountDue: ['amountdue', 'outstandingamount', 'outstandingbalance', 'openbalance'],
  currentAmount: ['currentamount', 'current', 'notyetdue'], aging30Amount: ['aging30amount', '130daysoverdue', '1to30daysoverdue', '130days'],
  aging60Amount: ['aging60amount', '3160daysoverdue', '31to60daysoverdue', '3160days'], aging90Amount: ['aging90amount', '6190daysoverdue', '61to90daysoverdue', '6190days'],
  aging90PlusAmount: ['aging90plusamount', 'morethan90daysoverdue', 'over90days', '91plusdays'], unknownDueDateAmount: ['unknownduedateamount', 'duedateunknown'],
};

export function suggestReceivablesMapping(headers, type) {
  return Object.fromEntries(IMPORT_FIELDS[type].map(({ key }) => {
    const matches = headers.filter(header => aliases[key].includes(normalized(header)));
    return [key, matches.length === 1 ? matches[0] : ''];
  }));
}

function safeCell(value) {
  if (value == null) return '';
  if (value instanceof Date) {
    if (!Number.isFinite(value.getTime())) throw new Error('The workbook contains an invalid date. Use YYYY-MM-DD text dates.');
    return value.toISOString().slice(0, 10);
  }
  if (typeof value === 'number' && (!Number.isFinite(value) || Math.abs(value) > Number.MAX_SAFE_INTEGER)) {
    throw new Error('The workbook contains a number beyond safe spreadsheet precision. Export that value as text.');
  }
  return String(value).trim();
}

function tableFromMatrix(matrix, name, sourceRows, cellMetadata) {
  if (!matrix.length) throw new Error('The selected sheet is empty.');
  const headers = matrix[0].map(safeCell);
  if (!headers.length || headers.some(header => !header)) throw new Error('Every source column must have a nonempty header in the first row.');
  if (new Set(headers).size !== headers.length) throw new Error('Source column headers must be unique.');
  if (headers.length > 100) throw new Error('Use a file with at most 100 columns.');
  const rows = matrix.slice(1).map((values, index) => ({
    sourceRow: sourceRows?.[index + 1] || index + 2,
    sourceSheet: name,
    values: Object.fromEntries(headers.map((header, column) => [header, safeCell(values[column])])),
    ...(cellMetadata ? { cells: Object.fromEntries(headers.map((header, column) => [header, cellMetadata[index + 1]?.[column] || null])) } : {}),
    extra: values.slice(headers.length).some(value => String(value ?? '').trim()),
  })).filter(row => Object.values(row.values).some(Boolean) || row.extra);
  if (!rows.length) throw new Error('The selected sheet has no data rows.');
  if (rows.some(row => row.extra)) throw new Error('A source row has more values than the header row. Check the file delimiters.');
  if (rows.length > MAX_IMPORT_ROWS) throw new Error(`Use at most ${MAX_IMPORT_ROWS} rows per import. Split larger files into batches with the same stable invoice and transaction IDs.`);
  return { name, headers, rows };
}

export function parseReceivablesCsv(text, name = 'CSV') {
  // dynamicTyping must remain false: transaction IDs and money are exact text.
  const source = text.replace(/^\uFEFF/, '');
  // Detect using populated records, then preserve blank records in the actual parse
  // so source row references remain stable.
  const delimiter = Papa.parse(source, { preview: 10, dynamicTyping: false, skipEmptyLines: 'greedy' }).meta.delimiter;
  const parsed = Papa.parse(source, { delimiter: delimiter || ',', dynamicTyping: false, skipEmptyLines: false });
  if (parsed.errors.length) throw new Error(`CSV could not be read: ${parsed.errors[0].message}`);
  const matrix = parsed.data;
  while (matrix.length && matrix[matrix.length - 1].every(value => !String(value).trim())) matrix.pop();
  return tableFromMatrix(matrix, name);
}

// OOXML numeric values must be read before SheetJS converts them to Number.
// Formatting is presentation only: neither formatted IDs nor rounded money may
// silently replace the source. Binary XLS lacks decimal lexemes, so its numeric
// financial cells are accepted only within Excel's 15-digit precision boundary.
function exactDecimal(lexeme) {
  const match = /^([+-]?)(\d+(?:\.\d*)?|\.\d+)(?:[eE]([+-]?\d+))?$/.exec(String(lexeme).trim());
  if (!match || match[2].length > 256 || (match[3] && !/^[+-]?\d{1,3}$/.test(match[3]))) throw new Error('The workbook contains an unsupported numeric value. Export that value as text.');
  const [whole, fraction = ''] = match[2].split('.');
  const exponent = Number(match[3] || 0);
  if (Math.abs(exponent) > 128) throw new Error('The workbook contains a number beyond supported precision. Export that value as text.');
  const digits = `${whole}${fraction}`;
  const point = whole.length + exponent;
  let value = point <= 0 ? `0.${'0'.repeat(-point)}${digits}` : point >= digits.length ? `${digits}${'0'.repeat(point - digits.length)}` : `${digits.slice(0, point)}.${digits.slice(point)}`;
  let [integer, decimal = ''] = value.split('.');
  integer = integer.replace(/^0+(?=\d)/, '') || '0';
  decimal = decimal.replace(/0+$/, '');
  value = `${integer}${decimal ? `.${decimal}` : ''}`;
  return `${match[1] === '-' && value !== '0' ? '-' : ''}${value}`;
}

function xmlPart(workbook, path) {
  const content = workbook.files?.[path]?.content;
  if (!content) throw new Error('The workbook numeric source could not be verified. Export a values-only workbook or CSV with exact text values.');
  const bytes = content instanceof Uint8Array ? content : new Uint8Array(content);
  if (bytes.length > 10 * 1024 * 1024) throw new Error('The expanded worksheet exceeds the safe import size. Export only the header and selected source rows.');
  const encoding = bytes[0] === 255 && bytes[1] === 254 ? 'utf-16le' : bytes[0] === 254 && bytes[1] === 255 ? 'utf-16be' : 'utf-8';
  const doc = new DOMParser().parseFromString(new TextDecoder(encoding, { fatal: true }).decode(bytes), 'application/xml');
  if (doc.getElementsByTagName('parsererror').length) throw new Error('The workbook contains invalid source XML. Export a values-only workbook or CSV.');
  return doc;
}
const elements = (node, name) => Array.from(node.getElementsByTagNameNS('*', name));
function numericSources(workbook) {
  const workbookPath = workbook.Directory?.workbooks?.find(path => /\.xml$/i.test(path))?.replace(/^\//, '');
  if (!workbookPath) return null;
  const parts = workbookPath.split('/'), filename = parts.pop(), folder = parts.join('/');
  const relationPath = `${folder ? `${folder}/` : ''}_rels/${filename}.rels`;
  const relationships = new Map(elements(xmlPart(workbook, relationPath), 'Relationship').map(item => [item.getAttribute('Id'), item]));
  const result = new Map();
  for (const sheet of elements(xmlPart(workbook, workbookPath), 'sheet')) {
    const id = sheet.getAttributeNS('http://schemas.openxmlformats.org/officeDocument/2006/relationships', 'id') || sheet.getAttribute('r:id');
    const relation = relationships.get(id);
    if (!relation || relation.getAttribute('TargetMode') === 'External' || !/\/worksheet$/.test(relation.getAttribute('Type') || '')) continue;
    const target = relation.getAttribute('Target') || '';
    const segments = target.startsWith('/') ? [] : [...parts];
    for (const part of target.split('/')) {
      if (!part || part === '.') continue;
      if (part === '..') { if (!segments.length) throw new Error('The workbook has an invalid worksheet path.'); segments.pop(); }
      else segments.push(part);
    }
    const cells = new Map();
    for (const cell of elements(xmlPart(workbook, segments.join('/')), 'c')) {
      const type = cell.getAttribute('t');
      if (type && type !== 'n') continue;
      const value = elements(cell, 'v')[0];
      if (!value) continue;
      const address = cell.getAttribute('r');
      if (cells.has(address)) throw new Error('The workbook contains duplicate numeric cell identities. Export a clean values-only file.');
      cells.set(address, { value: exactDecimal(value.textContent), lexeme: value.textContent.trim() });
    }
    result.set(sheet.getAttribute('name'), cells);
  }
  return result;
}

export function parseReceivablesWorkbook(buffer) {
  const workbook = XLSX.read(buffer, { type: 'array', cellDates: true, cellFormula: true, cellText: true, cellNF: true, bookFiles: true, sheetRows: MAX_IMPORT_ROWS + 2 });
  let sources;
  try { sources = numericSources(workbook); }
  catch (error) { return workbook.SheetNames.map(name => ({ name, error: error.message })); }
  return workbook.SheetNames.map(name => {
    const sheet = workbook.Sheets[name];
    const range = sheet['!ref'] && XLSX.utils.decode_range(sheet['!ref']);
    if (!range) return { name, error: 'This sheet is empty.' };
    if (sheet['!fullref']) return { name, error: `Use at most ${MAX_IMPORT_ROWS} rows per import. Export the header and selected source rows only.` };
    if (range.e.r > 10000 || range.e.c > 99) return { name, error: 'This sheet exceeds the supported range. Export only the header and relevant data rows.' };
    try {
      const matrix = [], metadata = [];
      for (let row = range.s.r; row <= range.e.r; row += 1) {
        const values = [], cells = [];
        for (let column = range.s.c; column <= range.e.c; column += 1) {
          const address = XLSX.utils.encode_cell({ r: row, c: column });
          const cell = sheet[address];
          if (cell?.f) throw new Error('Formula cells require a values-only export before import; cached formulas may be stale.');
          if (cell?.t === 'e') throw new Error('The sheet contains a spreadsheet error. Correct it before import.');
          safeCell(cell?.v); // Retain the existing non-finite / unsafe magnitude guard.
          const exact = sources?.get(name)?.get(address);
          if (cell?.t === 'n' && sources && !exact) throw new Error('A numeric worksheet cell has no verified source value. Export it as exact text.');
          values.push(cell?.t === 'n' && exact ? exact.value : cell?.v ?? '');
          cells.push(cell ? { type: cell.t, numericValue: cell.t === 'n' ? cell.v : null, displayed: cell.w ?? safeCell(cell.v), exactNumeric: exact?.value ?? null, numericLexeme: exact?.lexeme ?? null, numericSource: exact ? 'ooxml' : workbook.cfb ? 'binary-xls' : 'unverified' } : null);
        }
        matrix.push(values); metadata.push(cells);
      }
      return tableFromMatrix(matrix, name, matrix.map((_, index) => range.s.r + index + 1), metadata);
    } catch (error) { return { name, error: error.message }; }
  });
}

export async function readReceivablesFile(file) {
  if (!file || file.size > MAX_IMPORT_BYTES) throw new Error('Choose a CSV or Excel file no larger than 5 MB.');
  if (/\.csv$/i.test(file.name)) return [parseReceivablesCsv(await file.text(), file.name)];
  if (/\.xlsx?$/i.test(file.name)) return parseReceivablesWorkbook(await file.arrayBuffer());
  throw new Error('Choose a .csv, .xlsx or .xls file.');
}

const ID_FIELDS = new Set(['accountId', 'documentNumber', 'eventId', 'reversesEventId']);
const MONEY_FIELDS = new Set(['invoiceAmount', 'amount', 'amountDue', 'currentAmount', 'aging30Amount', 'aging60Amount', 'aging90Amount', 'aging90PlusAmount', 'unknownDueDateAmount']);
function mappedCellValue(row, header, key) {
  const value = row.values[header], cell = row.cells?.[header];
  if (!cell || value === '' || ['s', 'str'].includes(cell.type)) return value;
  if (ID_FIELDS.has(key)) {
    if (cell.type !== 'n' || !['ooxml', 'binary-xls'].includes(cell.numericSource) || !Number.isSafeInteger(cell.numericValue) || String(cell.numericValue) !== value || cell.displayed !== value) {
      throw new Error(`Source row ${row.sourceRow}: ${header} changes through spreadsheet formatting or numeric precision. Export this identifier as exact text before importing.`);
    }
  }
  if (MONEY_FIELDS.has(key)) {
    if (cell.type !== 'n') throw new Error(`Source row ${row.sourceRow}: ${header} must be exact numeric or text money, not a date or boolean.`);
    if (cell.numericSource !== 'ooxml') {
      const precision = value.replace(/^-/, '').replace('.', '').replace(/^0+/, '').length;
      if (cell.numericSource !== 'binary-xls' || precision > 15) throw new Error(`Source row ${row.sourceRow}: ${header} exceeds verifiable spreadsheet precision. Export the exact amount as text before importing.`);
    }
  }
  return value;
}

export function mappedReceivablesRows(table, mapping, type, { unknownDueDateDeclaredZero = false } = {}) {
  const required = IMPORT_FIELDS[type].filter(item => item.required && !mapping[item.key]);
  if (required.length) throw new Error(`Map the required columns: ${required.map(item => item.label).join(', ')}.`);
  const chosen = Object.values(mapping).filter(Boolean);
  if (new Set(chosen).size !== chosen.length) throw new Error('Each source column can map to only one field.');
  if (chosen.some(header => !table.headers.includes(header))) throw new Error('The mapping contains a column that is no longer in this file.');
  if (type === 'aging_summary' && !mapping.unknownDueDateAmount && !unknownDueDateDeclaredZero) throw new Error('Map the unknown-age balance or explicitly confirm that the report has no unknown-age balance (0).');
  return table.rows.map(row => {
    // Retain the reviewed source headers/values so the mapping can be audited.
    // Unmapped columns are intentionally omitted from this evidence.
    const mapped = Object.fromEntries(IMPORT_FIELDS[type].filter(({ key }) => mapping[key]).map(({ key }) => [key, mappedCellValue(row, mapping[key], key)]));
    const sourceValues = Object.fromEntries(chosen.map(header => [header, row.cells?.[header]?.type === 'n' ? row.cells[header].numericLexeme ?? row.values[header] : row.values[header]]));
    if (type === 'aging_summary' && !mapping.unknownDueDateAmount) sourceValues['User declaration: unknown-age balance'] = '0 (none reported)';
    if (type === 'aging_summary' && mapping.unknownDueDateAmount && !row.values[mapping.unknownDueDateAmount]) throw new Error(`Source row ${row.sourceRow} has a blank unknown-age balance. Supply its explicit value before importing.`);
    if (JSON.stringify(sourceValues).length > 12000) throw new Error(`Source row ${row.sourceRow} exceeds the supported evidence size. Shorten the mapped values before importing.`);
    return {
      sourceRow: row.sourceRow,
      sourceSheet: row.sourceSheet,
      sourceValues,
      ...mapped,
      ...(type === 'aging_summary' && !mapping.unknownDueDateAmount ? { unknownDueDateAmount: '0' } : {}),
    };
  });
}

export const receivablesErrorMessage = error => error?.response?.data?.message || error?.message || 'The operation could not be confirmed. Please retry.';

export const allocationEventKey = event => JSON.stringify([event.accountId, event.currency, event.eventId]);

export function reviewedAllocationRows(catalog, selections) {
  if (!catalog || !Array.isArray(catalog.events) || !Array.isArray(catalog.candidateInvoices)) throw new Error('Load the current unallocated transactions before reviewing allocations.');
  const rows = catalog.events.flatMap((event, index) => {
    const documentNumber = selections[allocationEventKey(event)];
    if (!documentNumber) return [];
    if (event.accountId !== catalog.accountId || !catalog.candidateInvoices.some(invoice => invoice.currency === event.currency && invoice.documentNumber === documentNumber)) {
      throw new Error('An allocation no longer matches this customer and currency. Reload the transactions.');
    }
    return [{ accountId: event.accountId, currency: event.currency, eventId: event.eventId, documentNumber,
      sourceRow: index + 1, sourceSheet: 'Allocation review', sourceValues: {
        accountId: event.accountId, currency: event.currency, eventId: event.eventId, selectedInvoice: documentNumber,
        retainedAmount: event.amount, retainedPaymentDate: event.paymentDate, originalImportId: event.importId || '',
      } }];
  });
  if (!rows.length) throw new Error('Choose an invoice for at least one retained transaction.');
  if (rows.length > MAX_IMPORT_ROWS) throw new Error(`Review at most ${MAX_IMPORT_ROWS} allocations in one commit.`);
  return rows;
}
